import { ClientContext } from "@database/contexts";
import { useCallback, useContext, useState } from "react";
import { useOrganisation } from "./useOrganisation";
import { clientService } from "@database/services";
import { Client, ErrorType } from "@utilities/types";

export const useClient = () => {
    
    const {
        clients, setClients,
        selectedClient, setSelectedClient,
    } = useContext(ClientContext);
    const { organisation } = useOrganisation();

    const [findClientError, setFindClientError] = useState<ErrorType | null>(null);
    const [findClientLoading, setFindClientLoading] = useState<boolean>(false);
    const [getAllClientsError, setGetAllClientsError] = useState<ErrorType | null>(null);
    const [getAllClientsLoading, setGetAllClientsLoading] = useState<boolean>(false);
    const [createClientError, setCreateClientError] = useState<ErrorType | null>(null);
    const [createClientLoading, setCreateClientLoading] = useState<boolean>(false);
    const [createClientSuccess, setCreateClientSuccess] = useState<boolean>(false);
    const [updateClientError, setUpdateClientError] = useState<ErrorType | null>(null);
    const [updateClientLoading, setUpdateClientLoading] = useState<boolean>(false);
    const [deleteClientError, setDeleteClientError] = useState<ErrorType | null>(null);
    const [deleteClientLoading, setDeleteClientLoading] = useState<boolean>(false);

    const onFindClient = useCallback(async(search: string)=> {
        if(!organisation) return;

        setFindClientLoading(true);
        const req = await clientService.findClient(search, organisation._id);
        if(req.code === 200) {
            setFindClientError(null);
            setClients(req.data);
        } else {
            setFindClientError({
                message: (req.code === 400 || req.code === 404) ?
                    "Clients introuvables" : "Une erreur s'est produite lors de la recherche des clients",
            });
        }
        setFindClientLoading(false);
    }, [organisation, setClients]);

    const onGetAllClients = useCallback(async() => {
        if(!organisation) return;

        setGetAllClientsLoading(true);
        const req = await clientService.getAllClients(organisation._id);
        if(req.code === 200) {
            setGetAllClientsError(null);
            setClients(req.data);
        } else {
            setGetAllClientsError({
                message: (req.code === 400 || req.code === 404) ?
                    "Clients introuvables" : "Une erreur s'est produite lors de la recherche des clients",
            });
        }
        setGetAllClientsLoading(false);
    }, [organisation, setClients]);

    const onCreateClient = useCallback(async(clientData: Partial<Client>) => {
        if(!organisation) return;

        setCreateClientLoading(true);
        const req = await clientService.createClient({
            ...clientData,
            _organisationID: organisation._id,
        });
        if(req.code === 201) {
            setCreateClientError(null);
            setCreateClientSuccess(true);
        } else {
            setCreateClientError({
                message: (req.code === 400) ?
                    "Impossible de creer le client" : "Une erreur s'est produite lors de la recherche des clients",
            });
        }
        setCreateClientLoading(false);
    }, [organisation]);

    const onUpdateClient = useCallback(async(clientId: string, clientData: Partial<Client>) => {
        setUpdateClientLoading(true);
        const req = await clientService.updateClient(clientId, clientData);
        if(req.code === 200) {
            setUpdateClientError(null);
            await onGetAllClients();
        } else {
            setUpdateClientError({
                message: (req.code === 400 || req.code === 404) ?
                    "Impossible de mettre à jour le client" : "Une erreur s'est produite lors de la recherche des clients",
            });
        }
        setUpdateClientLoading(false);
    }, [onGetAllClients]);

    const onDeleteClient = useCallback(async(clientId: string) => {
        setDeleteClientLoading(true);
        const req = await clientService.deleteClient(clientId);
        if(req.code === 200) {
            setDeleteClientError(null);
            await onGetAllClients();
        } else {
            setDeleteClientError({
                message: (req.code === 400 || req.code === 404) ?
                    "Impossible de supprimer le client" : "Une erreur s'est produite lors de la recherche des clients",
            });
        }
        setDeleteClientLoading(false);
    }, [onGetAllClients]);

    return {
        clients, selectedClient, findClientLoading,
        findClientError, getAllClientsLoading,
        getAllClientsError, createClientLoading,
        createClientError, updateClientLoading,
        updateClientError, deleteClientLoading,
        createClientSuccess, setCreateClientSuccess,
        deleteClientError, onGetAllClients,
        setSelectedClient, onFindClient,
        onCreateClient, onUpdateClient, onDeleteClient
    };
};
